import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { AuthGate } from "@/components/auth/auth-gate";
import { PortfolioValueCard } from "./components/portfolio-value-card";
import { AccountHealthCard } from "./components/account-health-card";
import { PnlChartCard } from "./components/pnl-chart-card";
import { RecentActivityCard } from "./components/recent-activity-card";
import { DailyPnlTable } from "./components/daily-pnl-table";
import { PortfolioHeaderCard, type PortfolioTab } from "./components/portfolio-header-card";
import { TransferHistoryTab } from "./components/transfer-history-tab";
import { HistoryTab } from "./components/history-tab";
import { FundingTab } from "./components/funding-tab";
import { type DateRangeValue } from "@/components/ui/date-range-selector";
import { AccountProvider, useAccountContext } from "./account-context";
import { usePnlHistory } from "./hooks/use-pnl-history";

export const AccountPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<PortfolioTab>("overview");
  const [dateRange, setDateRange] = useState<DateRangeValue>("all");
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);

  // Read tab from URL on mount and when URL changes
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      // Map URL param to actual tab value
      const tabMap: Record<string, PortfolioTab> = {
        overview: "overview",
        transfers: "transfers",
        history: "trades", // URL uses "history", internal uses "trades"
        funding: "funding",
      };
      if (tabMap[tabParam]) {
        setActiveTab(tabMap[tabParam]);
      }
    }
  }, [searchParams]);

  // Update URL when tab changes
  const handleTabChange = (tab: PortfolioTab) => {
    setActiveTab(tab);
    const urlTab = tab === "trades" ? "history" : tab;
    if (urlTab === "overview") {
      searchParams.delete("tab");
    } else {
      searchParams.set("tab", urlTab);
    }
    setSearchParams(searchParams, { replace: true });
  };

  // Scroll detection for sticky header (window scroll)
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      setIsHeaderSticky(scrollTop > 20);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <AuthGate
      title="Account Overview"
      description="Connect to view your account, real-time P&L, and trading statistics."
    >
      <AccountProvider dateRange={dateRange}>
        <AccountPageContent
          activeTab={activeTab}
          setActiveTab={handleTabChange}
          dateRange={dateRange}
          setDateRange={setDateRange}
          isHeaderSticky={isHeaderSticky}
        />
      </AccountProvider>
    </AuthGate>
  );
};

// Content component that uses the account context
type AccountPageContentProps = {
  activeTab: PortfolioTab;
  setActiveTab: (tab: PortfolioTab) => void;
  dateRange: DateRangeValue;
  setDateRange: (range: DateRangeValue) => void;
  isHeaderSticky: boolean;
};

const AccountPageContent = ({
  activeTab,
  setActiveTab,
  dateRange,
  setDateRange,
  isHeaderSticky,
}: AccountPageContentProps) => {
  const { deposits, withdrawals, fills, fundingPayments, orders, isLoading } = useAccountContext();
  const { pnlHistory } = usePnlHistory({ fills, fundingPayments });

  return (
    <main className="flex-1 p-4 md:p-6">
      <div className="max-w-[1600px] mx-auto space-y-4 md:space-y-6">
        {/* Header Card with Tabs and Date Range */}
        <PortfolioHeaderCard
          activeTab={activeTab}
          onTabChange={setActiveTab}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          isSticky={isHeaderSticky}
        />

        {/* Overview Tab Content */}
        {activeTab === "overview" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {/* Row 1: Account Value (2 cols) + Account Health */}
            <div className="md:col-span-2">
              <PortfolioValueCard />
            </div>
            <div>
              <AccountHealthCard />
            </div>

            {/* Row 2: P&L Chart (2 cols) + Recent Activity */}
            <div className="md:col-span-2">
              <PnlChartCard dateRange={dateRange} />
            </div>
            <div>
              <RecentActivityCard dateRange={dateRange} />
            </div>

            {/* Row 3: Daily P&L Table (full width) */}
            <div className="md:col-span-2 lg:col-span-3">
              <DailyPnlTable pnlHistory={pnlHistory} />
            </div>
          </div>
        )}

        {/* Transfers Tab Content */}
        {activeTab === "transfers" && (
          <TransferHistoryTab
            deposits={deposits}
            withdrawals={withdrawals}
            isLoading={isLoading}
          />
        )}

        {/* History Tab Content */}
        {activeTab === "trades" && (
          <HistoryTab
            fills={fills}
            orders={orders}
            isLoading={isLoading}
          />
        )}

        {/* Funding Tab Content */}
        {activeTab === "funding" && (
          <FundingTab
            fundingPayments={fundingPayments}
            isLoading={isLoading}
          />
        )}
      </div>
    </main>
  );
};
